import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileText, FolderOpen, Stethoscope, Headphones, Briefcase, Video, Users,
  Mail, ArrowRight, Bookmark, AlertCircle, Clock, Building2, Megaphone
} from "lucide-react";
import api from "@/lib/api";

const STAT_CARDS = [
  { key: "articles", label: "Articles", icon: FileText, color: "text-blue-600", bg: "bg-blue-50", link: "/admin/articles" },
  { key: "categories", label: "Categories", icon: FolderOpen, color: "text-purple-600", bg: "bg-purple-50", link: "/admin/categories" },
  { key: "doctors", label: "Doctors", icon: Stethoscope, color: "text-teal-600", bg: "bg-teal-50", link: "/admin/doctors" },
  { key: "podcasts", label: "Podcasts", icon: Headphones, color: "text-orange-600", bg: "bg-orange-50", link: "/admin/podcasts" },
  { key: "jobs", label: "Jobs", icon: Briefcase, color: "text-green-600", bg: "bg-green-50", link: "/admin/jobs" },
  { key: "videos", label: "Videos", icon: Video, color: "text-red-600", bg: "bg-red-50", link: "/admin/videos" },
  { key: "users", label: "Users", icon: Users, color: "text-indigo-600", bg: "bg-indigo-50" },
  { key: "subscribers", label: "Subscribers", icon: Mail, color: "text-pink-600", bg: "bg-pink-50", link: "/admin/newsletter" },
  { key: "bookmarks", label: "Bookmarks", icon: Bookmark, color: "text-amber-600", bg: "bg-amber-50" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/articles?include_unpublished=true&limit=8"),
    ]).then(([statsRes, artRes]) => {
      setStats(statsRes.data);
      setRecentArticles(artRes.data.articles || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-foreground">Dashboard</h1>
        <p className="text-sm font-sans text-muted-foreground mt-1">Overview of your medical media platform</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
            {STAT_CARDS.map((card) => (
              <Card key={card.key} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-card-${card.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`w-8 h-8 ${card.bg} dark:bg-slate-700 flex items-center justify-center`}>
                      <card.icon className={`h-4 w-4 ${card.color}`} />
                    </div>
                    {card.link && (
                      <Link to={card.link} className="text-[10px] font-sans text-[#1E88E5] hover:underline flex items-center gap-0.5">
                        Manage <ArrowRight className="h-2.5 w-2.5" />
                      </Link>
                    )}
                  </div>
                  <p className="text-2xl font-serif font-bold text-foreground">{stats?.[card.key] || 0}</p>
                  <p className="text-[11px] font-sans text-muted-foreground mt-0.5">{card.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="mb-10">
            <h2 className="text-xl font-serif font-bold text-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/admin/articles/new" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 hover:border-[#1E88E5] transition-colors group" data-testid="quick-new-article">
                <FileText className="h-5 w-5 text-[#1E88E5] mb-2" />
                <h3 className="font-sans font-semibold text-sm text-foreground group-hover:text-[#1E88E5] transition-colors">New Article</h3>
                <p className="text-xs font-sans text-muted-foreground mt-1">Create medical content</p>
              </Link>
              <Link to="/admin/categories" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 hover:border-[#1E88E5] transition-colors group" data-testid="quick-categories">
                <FolderOpen className="h-5 w-5 text-purple-500 mb-2" />
                <h3 className="font-sans font-semibold text-sm text-foreground group-hover:text-[#1E88E5] transition-colors">Manage Categories</h3>
                <p className="text-xs font-sans text-muted-foreground mt-1">{stats?.categories || 0} categories</p>
              </Link>
              <Link to="/admin/newsletter" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 hover:border-[#1E88E5] transition-colors group" data-testid="quick-newsletter">
                <Mail className="h-5 w-5 text-pink-500 mb-2" />
                <h3 className="font-sans font-semibold text-sm text-foreground group-hover:text-[#1E88E5] transition-colors">Send Newsletter</h3>
                <p className="text-xs font-sans text-muted-foreground mt-1">{stats?.subscribers || 0} subscribers</p>
              </Link>
              <Link to="/admin/breaking-news" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-5 hover:border-[#1E88E5] transition-colors group" data-testid="quick-breaking-news">
                <AlertCircle className="h-5 w-5 text-red-500 mb-2" />
                <h3 className="font-sans font-semibold text-sm text-foreground group-hover:text-[#1E88E5] transition-colors">Breaking News</h3>
                <p className="text-xs font-sans text-muted-foreground mt-1">Manage ticker</p>
              </Link>
            </div>
          </div>

          {/* Recent Articles */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif font-bold text-foreground">Recent Articles</h2>
              <Link to="/admin/articles" className="text-xs font-sans text-[#1E88E5] hover:underline flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
              {recentArticles.slice(0, 6).map(art => (
                <div key={art.id} className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="min-w-0 flex-1 mr-4">
                    <Link to={`/admin/articles/${art.id}/edit`} className="font-sans font-medium text-sm text-foreground hover:text-[#1E88E5] transition-colors line-clamp-1">
                      {art.title}
                    </Link>
                    <div className="flex items-center gap-2 mt-1 text-[11px] font-sans text-muted-foreground">
                      {art.author_name && <span>{art.author_name}</span>}
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{art.reading_time || "-"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={art.is_published ? "default" : "secondary"} className={`text-[10px] font-sans ${art.is_published ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}`}>
                      {art.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
